import React, { FC, useEffect, useState } from "react";
import Modal from "react-modal";

import Drawer from "../../../components/Drawer";
import ExpandableRow from "../../../components/ExpandableRow";
import { ReactComponent as IconFilter } from "../../../assets/icons/filter.svg";
import { ReactComponent as IconEdit } from "../../../assets/icons/edit.svg";
import { ReactComponent as IconKey } from "../../../assets/icons/key.svg";
import { ReactComponent as IconTrash } from "../../../assets/icons/trash.svg";
import ConnectionsModalPassword from "./ModalPassword/ConnectionsModalPassword";
import ConnectionsModal from "./Modal/ConnectionsModal";
import { getParameterByName } from "../../../utils/toSearchParams";
import { deserialize } from "../../../api/jsonApiParser";
import {
  setConnectionsPageNo,
  fetchConnectionsList,
  deleteConnection,
  getConnectionById,
  incrementConnectionsPageNo,
} from "./ducks";
import { TDispatch, TRootState } from "../../../redux/types";
import { Checkbox } from "@components/Checkbox";
import { useSwal } from "@hooks/useSwal";
import { useHistory, useLocation } from "react-router-dom";
import { useFormatMessage } from "@hooks/formatMessage";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Flex } from "reflexbox";
import { OldInput } from "@components/OldInput";
import { Button } from "@components/Button";
import { Text } from "@components/Text";
import { AddButton } from "@components/AddButton";
import { ScrollableList } from "@components/ScrollableList";
import { Column } from "@components/ExpandableRow/styles";
import "../../../styles/index.scss";
import "./Connections.scss";

enum formMode {
  edit = "edit",
  password = "password",
}

const Connections: FC = () => {
  const [filtersOpened, setFiltersOpened] = useState(window.innerWidth >= 1400);
  const [modalContent, setModalContent] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(undefined);
  const [addConnectionForm, setaddConnectionForm] = useState(undefined);
  const { swalConfirmationMessage, swalAlertWithCallback } = useSwal();
  const [showMoreSpinner, setShowMoreSpinner] = useState(false);
  const [connectionsFilter, setConnectionsFilter] = useState({
    GenericText: null,
  });

  const history = useHistory();
  const location = useLocation();
  const formatMessage = useFormatMessage();
  const dispatch: TDispatch = useDispatch();

  const { connectionsList, moreConnections, connectionsPageNo } = useSelector(
    ({ ConnectionsReducer: { connections, moreConnections, connectionsPageNo } }: TRootState) => ({
      connectionsList: connections,
      moreConnections,
      connectionsPageNo,
    }),
    shallowEqual
  );

  if (location.search && location.search.length > 0) {
    setModalContent(getParameterByName("content", location.search));
    setModalIsOpen(!!modalContent);
  }

  useEffect(() => {
    dispatch(fetchConnectionsList()).then(() => dispatch(incrementConnectionsPageNo()));
  }, [dispatch]);

  const handleLoadMore = () => {
    if (!showMoreSpinner && moreConnections) {
      setShowMoreSpinner(true);
      dispatch(fetchConnectionsList(connectionsPageNo, 10, connectionsFilter)).then(() => {
        setShowMoreSpinner(false);
        dispatch(incrementConnectionsPageNo());
      });
    }
  };

  const changeFilterOpened = () => {
    setFiltersOpened(prevState => !prevState);
  };

  const filterConnections = () => {
    dispatch(setConnectionsPageNo(1));
    dispatch(fetchConnectionsList(1, 10, connectionsFilter));
  };

  const clearFilters = () => {
    setConnectionsFilter({
      GenericText: null,
    });

    dispatch(setConnectionsPageNo(1));
    dispatch(fetchConnectionsList(1, 10));
  };

  const openModal = (content, message?, entry?) => {
    getConnectionById(entry.id).then((ret: any) => {
      if (ret) {
        delete ret.data.attributes.password;
        setaddConnectionForm(deserialize(ret.data));
        setModalIsOpen(true);
        setModalContent(content);
      }
    });
  };

  const saveHistoryAndFilterConnections = () => {
    history.push({
      search: "",
    });
    filterConnections();
  };

  const closeModal = () => {
    setModalIsOpen(false);
    saveHistoryAndFilterConnections();

    // TODO: this.props.fetchCompanies().then(res => this.setState({ parentCompanyList: res }));
    // TODO: change the location of the list to the modal component
  };

  const showSuccessMessage = (action, callbackFunction) => {
    swalAlertWithCallback(
      {
        text: `Connection has been ${action}!`,
        type: "success",
        confirmationText: "Ok!",
      },
      callbackFunction
    );
  };

  const remove = id => {
    swalConfirmationMessage({
      text: "Do you really want to remove this connection?",
      type: "info",
      cancelationText: "Cancel",
      confirmationText: "Remove connection",
    }).then(value => {
      if (value) {
        deleteConnection(id).then(ret => {
          if (ret) showSuccessMessage("removed", saveHistoryAndFilterConnections);
        });
      }
    });
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Large Modal"
        className={"large-modal"}
        overlayClassName={"large-modal-overlay"}
      >
        {modalIsOpen && modalContent !== formMode.password ? (
          <ConnectionsModal closeModal={closeModal} initialData={addConnectionForm} />
        ) : (
          <ConnectionsModalPassword closeModal={closeModal} initialData={addConnectionForm} />
        )}
      </Modal>

      <Flex pl={["10px", "80px"]} pr={["10px", 0]} height="100%" width="100%">
        <Flex flexDirection="column" width="100%">
          <AddButton
            id="addConnectionBtn"
            p="0 32px"
            m="32px 0"
            label={formatMessage("app.addnewconnection", "Add Connection")}
            onClick={() => {
              setaddConnectionForm({});
              setModalIsOpen(true);
            }}
          />
          <ScrollableList
            items={connectionsList}
            onLoadMore={handleLoadMore}
            renderHeader={
              <>
                <Column>
                  <Text>{formatMessage("app.user", "User")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.webServiceName", "Web Service")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.dbName", "Database")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.hostName", "Host")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.databaseTypeName", "Database Type")}</Text>
                </Column>
                <Column icon>
                  <Text>
                    <div
                      id="toggleFilterArea"
                      className="icon-filter"
                      tabIndex={0}
                      onClick={e => {
                        e.stopPropagation();
                        changeFilterOpened();
                      }}
                    >
                      <IconFilter />
                    </div>
                  </Text>
                </Column>
              </>
            }
            renderItem={(item, index) => (
              <ExpandableRow
                data-cy="connectionRow"
                key={"connectionRow" + index}
                id={item.id as string}
                translucent={false}
                expandable={false}
                top={
                  <React.Fragment>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.username}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.webserviceName}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.dbName}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.hostName}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.databaseTypeName}
                        </Text>
                      </div>
                    </Column>
                    <Column icon>
                      <div className="d-flex flex-row showChildOnHover">
                        <div
                          data-cy="changeEntry"
                          className="iconWrapper pointer large"
                          title="Edit Connection"
                          style={{}}
                          onClick={() => openModal(formMode.edit, "", item)}
                        >
                          <IconEdit />
                        </div>
                        <div
                          data-cy="changePasswordEntry"
                          className="iconWrapper pointer large"
                          title="Edit Password"
                          style={{}}
                          onClick={() => openModal(formMode.password, "", item)}
                        >
                          <IconKey />
                        </div>
                        <div
                          data-cy="deleteEntry"
                          className="iconWrapper pointer large"
                          title="Delete Connection"
                          style={{}}
                          onClick={() => remove(item.id)}
                        >
                          <IconTrash />
                        </div>
                      </div>
                    </Column>
                  </React.Fragment>
                }
                more={
                  <React.Fragment>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.tenantName}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.schemaDb}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.port}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Checkbox
                          id="TableShowSqlInput"
                          name="TableShowSqlInput"
                          readOnly={true}
                          value={(item.showSql as string) || ""}
                          checked={item.showSql as boolean}
                          label={formatMessage("app.showSql", "Show Sql")}
                          wrapperProps={{ width: "20%" }}
                        />
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Checkbox
                          id="TableIsActiveInput"
                          name="TableIsActiveInput"
                          readOnly={true}
                          value={(item.isActive as string) || ""}
                          checked={item.isActive as boolean}
                          label={formatMessage("app.isActive", "Is Active")}
                          wrapperProps={{ width: "20%" }}
                        />
                      </div>
                    </Column>
                  </React.Fragment>
                }
              />
            )}
          />
        </Flex>
        <Drawer id="filterArea" opened={filtersOpened}>
          <div className="paddingContainer">
            <p className="colorGrey mb-1">Dynamic Filter</p>
            <div data-cy="dynamicFilterByField">
              <OldInput
                id="dynamicSearch"
                name="dynamicSearch"
                key="dynamicSearch"
                inputWrapperClass="text-input filter-input"
                placeholder={formatMessage("app.anyfield", "Any Field")}
                value={connectionsFilter.GenericText}
                onChange={(name, value) =>
                  setConnectionsFilter(prevState => ({ ...prevState, GenericText: value }))
                }
                validationMessageConfig={null /* this.validationMessageConfig() */}
                type="text"
                inputWrapperStyle={{}}
                filtering
              />
            </div>

            <div className="breaker mb-5" />

            <div className="modal-buttons">
              <Button id="buttonClearSearch" textButton mr="10px" onClick={clearFilters}>
                {formatMessage("app.clear", "Clear")}
              </Button>

              <Button id="buttonSearch" textButton mr="10px" onClick={filterConnections}>
                {formatMessage("app.search", "Search")}
              </Button>
            </div>
          </div>
        </Drawer>
      </Flex>
    </>
  );
};

export default Connections;
